import { useCallback } from 'react';
import type { WorkspacePresetsFieldsFragment } from 'generated/graphql';
import { useWorkspaceMutations } from './useWorkspaceMutations';
import { WithApolloContext } from 'modules/Apollo/types';

export const getMostRecentlyUpdatedPreset = (
  workspacePresets: readonly WorkspacePresetsFieldsFragment[]
): WorkspacePresetsFieldsFragment | undefined => {
  if (workspacePresets.length === 0) {
    return undefined;
  }

  return workspacePresets.reduce((mostRecent, current) => {
    const mostRecentTime = new Date(mostRecent.updatedAt).getTime();
    const currentTime = new Date(current.updatedAt).getTime();
    return currentTime > mostRecentTime ? current : mostRecent;
  });
};

export const useWorkspaceActions = ({ context }: WithApolloContext) => {
  const { updateWorkspacePreset } = useWorkspaceMutations({ context });

  const touchWorkspacePreset = useCallback(
    async (smid: string, workspacePresets: readonly WorkspacePresetsFieldsFragment[]) => {
      const presetToTouch = workspacePresets.find((preset) => preset.smid === smid);

      if (!presetToTouch) {
        return;
      }

      await updateWorkspacePreset({
        variables: {
          smid,
          payload: {
            instanceId: presetToTouch.instanceId,
            name: presetToTouch.name,
            windows: presetToTouch.windows.map(({ __typename, ...win }) => win),
          },
        },
      });
    },
    [updateWorkspacePreset]
  );

  const handlePresetSelection = useCallback(
    async (
      workspacePreset: WorkspacePresetsFieldsFragment,
      workspacePresets: readonly WorkspacePresetsFieldsFragment[],
      applyWorkspacePreset: (preset: WorkspacePresetsFieldsFragment) => void
    ) => {
      // First touch the preset to update its recently used time
      await touchWorkspacePreset(workspacePreset.smid, workspacePresets);
      // Then apply the workspace preset
      applyWorkspacePreset(workspacePreset);
    },
    [touchWorkspacePreset]
  );

  return {
    touchWorkspacePreset,
    handlePresetSelection,
  };
};
